import { compareModelKey } from "../../decorators/StoreIdEquality";
import {
  getObjectStoreId,
  getObjectStoreIdString,
  stringifyKey,
  stringifyModelKey,
} from "../../decorators/StoreIdHelper";
import { StoreName } from "../../decorators/StoreNameDecorator";
import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../../models/AcceleratorDoc";
import { AcceleratorDocMetadata } from "../../models/AcceleratorDocMetadata";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import Model, { ModelStoreKey } from "../../models/Model";
import { AcceleratorDocExtractor } from "../../services/AcceleratorDocExtractor";
import { AcceleratorDocMetadataStore } from "./AcceleratorDocMetadataStore";
import { AcceleratorDocStore } from "./AcceleratorDocStore";
import { ModelStore } from "./ModelStore";

@StoreName("AcceleratorDocModelStore")
class AcceleratorDocModelStoreClass {
  /**
   * Stores the Unknown Models for the AcceleratorDocs, keyed by stringified AcceleratorDocStoreKey.
   */
  private unknownMap: Map<string, Model | ModelStoreKey>;
  /**
   * Stores the ModelStoreKeys of _!Unknown_ Models for a the AcceleratorDocs, keyed by stringified
   * AcceleratorDocStoreKey.
   */
  private map: Map<string, ModelStoreKey>;
  /**
   * Stores an array of AcceleratorDocKeys for any given key. Key could be either a ModelStoreKey, or an
   * AcceleratorDocStoreKey, depending if the Model is unknown or not.
   */
  private revMap: Map<string, AcceleratorDocStoreKey[]>;

  constructor() {
    this.unknownMap = new Map();
    this.map = new Map();
    this.revMap = new Map();
  }

  private getMapEntry(key: AcceleratorDocStoreKey): ModelStoreKey | undefined {
    return this.map.get(stringifyKey(key));
  }

  private getUnknownMapEntry(
    key: AcceleratorDocStoreKey,
  ): Model | ModelStoreKey | undefined {
    return this.unknownMap.get(stringifyKey(key));
  }

  private setMapEntry(
    key: AcceleratorDocStoreKey,
    modelKey: ModelStoreKey,
  ): void {
    this.map.set(stringifyKey(key), modelKey);
  }

  private setUnknownMapEntry(
    key: AcceleratorDocStoreKey,
    model: Model | ModelStoreKey,
  ): void {
    this.unknownMap.set(stringifyKey(key), model);
  }

  private deleteMapEntry(key: AcceleratorDocStoreKey): boolean {
    return this.map.delete(stringifyKey(key));
  }

  private deleteUnknownMapEntry(key: AcceleratorDocStoreKey): boolean {
    return this.unknownMap.delete(stringifyKey(key));
  }

  /**
   * Builds a placeholder unknown Model for an AcceleratorDoc that doesn't have a real one
   * associated yet, caches it into unknownMap, and returns it.
   */
  private createAndCacheUnknownModel(
    acceleratorDocKey: AcceleratorDocStoreKey,
    batchClassIdentifier: string,
    docType: string,
  ): Model {
    const unknownModel = Model.createTest({
      name: "",
      batchClassIdentifier,
      docType: docType || "Unknown",
      templateType: "UNKNOWN",
    });
    this.setUnknownMapEntry(acceleratorDocKey, unknownModel);
    return unknownModel;
  }

  /**
   * Remove the association and reverse-map association between an AcceleratorDoc and a Model.
   *
   * @param acceleratorDocKey
   * @param modelKey
   * @returns
   */
  removeAssociation(
    acceleratorDocKey: AcceleratorDocStoreKey,
    modelKey: ModelStoreKey,
  ): boolean {
    let removed = false;
    removed = this.deleteUnknownMapEntry(acceleratorDocKey) || removed;
    removed = this.deleteMapEntry(acceleratorDocKey) || removed;
    this.removeDocFromReverseMap(modelKey, acceleratorDocKey);

    return removed;
  }

  getDocuments(model: Model | ModelStoreKey): AcceleratorDocStoreKey[] {
    const isUnknown = "unknown" in model ? model.unknown : model.name === "";
    if (isUnknown) {
      return [];
    }
    const targetKey = "storeId" in model ? (model.storeId as ModelStoreKey) : model;
    const results: AcceleratorDocStoreKey[] = [];
    for (const [keyStr, docKeys] of this.revMap.entries()) {
      try {
        const parsedKey = JSON.parse(keyStr) as ModelStoreKey;
        if (compareModelKey(parsedKey, targetKey)) {
          results.push(...docKeys);
        }
      } catch {
        // Ignore non-JSON keys
      }
    }
    const uniqueMap = new Map<string, AcceleratorDocStoreKey>();
    results.forEach((k) => uniqueMap.set(stringifyKey(k), k));
    return Array.from(uniqueMap.values());
  }

  /**
   * Return the Model associated with an AcceleratorDoc
   */
  getModel(
    acceleratorDoc: AcceleratorDoc | AcceleratorDocMetadata,
  ): Model | undefined {
    const docKey = acceleratorDoc.storeId;
    const docModelName = acceleratorDoc.getModelName();
    const mapKey = this.getMapEntry(docKey);

    if (mapKey) {
      const model = ModelStore.getWithoutLookup(mapKey);
      if (model) {
        return model;
      }
    }

    if (docModelName === "") {
      const returnObj = this.getUnknownMapEntry(docKey);
      if (!returnObj) {
        const batchClassId =
          acceleratorDoc instanceof AcceleratorDoc
            ? acceleratorDoc.getBatchClassId()
            : acceleratorDoc.batchClassId;
        return this.createAndCacheUnknownModel(
          docKey,
          batchClassId,
          acceleratorDoc.classifiedDocType,
        );
      }
      if (!("storeId" in returnObj)) {
        return this.createAndCacheUnknownModel(
          docKey,
          returnObj.batchClassIdentifier,
          returnObj.docType ?? "",
        );
      }
      return returnObj;
    }

    return undefined;
  }

  /**
   *
   * requires AcceleratorDocMetadataStore has key
   * @param model
   * @param acceleratorDoc passed so reveseMap keys for unknown models can be updated
   */
  markModelDocumentsAsModelChanged(
    model: Model,
    acceleratorDocKey: AcceleratorDocStoreKey,
  ) {
    // if (!model.changed) return;
    // iterate reverse map
    const reverseMapKey = this.getReverseMapStoreKey(
      model.storeId,
      acceleratorDocKey,
    );
    if (!this.revMap.has(reverseMapKey)) {
      this.revMap.set(reverseMapKey, []);
    }
    const allAccDocKeys = this.revMap.get(reverseMapKey)!;
    // TODO: test how only getting required parts of loop works
    // filter out deleted AcceleratorDocs
    const newAccDocKeys = allAccDocKeys.map((key) => {
      const acceleratorDocMetadata =
        AcceleratorDocMetadataStore.getWithoutLookup(key);
      if (acceleratorDocMetadata) {
        // set AceleratorDocMetadata
        acceleratorDocMetadata.setModelChanged(model.changed);
        return key;
      }
      // throw new Error('AcceleratorDocMetadata not found, supposed to mark Model as changed ?')
      return null;
    });
    this.revMap.set(
      reverseMapKey,
      newAccDocKeys.filter((key): key is AcceleratorDocStoreKey => key != null),
    );
  }

  /**
   * Updates the Model associated with an AcceleratorDoc. Returns the newly merged Model
   * Always call removeAssociation() before the model has been renamed and this methood is called.
   * TODO: ensure that modifying the DLF of an Unknown model does not cause update the other Unknown Models
   */
  setModel(acceleratorDocKey: AcceleratorDocStoreKey, modified: Model): Model {
    if (modified._firstModified == undefined) {
      modified._firstModified = { ...acceleratorDocKey };
    }
    let returnModel = modified;
    if (modified.getName() !== "") {
      returnModel = this.mergeModel(modified);
    }
    this.associate(acceleratorDocKey, returnModel);
    this.markModelDocumentsAsModelChanged(modified, acceleratorDocKey);

    return returnModel;
  }

  associate(
    acceleratorDocKey: AcceleratorDocStoreKey,
    model: Model | ModelStoreKey,
  ) {
    //@ts-expect-error
    const modelKey: ModelStoreKey = model.storeId ?? model;
    this.deleteMapEntry(acceleratorDocKey);
    this.deleteUnknownMapEntry(acceleratorDocKey);
    if (modelKey.name === "") {
      this.setUnknownMapEntry(acceleratorDocKey, model);
    } else {
      this.setMapEntry(acceleratorDocKey, modelKey);
    }

    const docMetadata =
      AcceleratorDocMetadataStore.getWithoutLookup(acceleratorDocKey);
    if (docMetadata) {
      docMetadata.setClassifiedModelName(modelKey.name);
    }

    const doc = AcceleratorDocStore.getWithoutLookup(acceleratorDocKey);
    if (doc) {
      doc.setModelName(modelKey.name);
    }
    this.addNewDocToReverseMap(modelKey, acceleratorDocKey);
  }

  /**
   * Merges the provided Model onto the Model that already exists in the store.
   * NOT TO BE USED FOR UNKNOWN MODELS
   * @param modified
   */
  mergeModel(modified: Model) {
    if (modified.getName() === "") {
      //console.warn('Attempting to merge Model with empty name into ModelStore');
    }
    // need to check if modified exists in ModelStore
    const optModel = ModelStore.getWithoutLookup(modified.storeId);
    let finalModel: Model;
    if (optModel) {
      finalModel = optModel;
      finalModel.docType = modified.docType; // merge the Models
      finalModel.templateType = modified.templateType;
      finalModel.status = modified.status;
      finalModel.enabled = modified.enabled;
      finalModel.forceValidate = modified.forceValidate;
      finalModel.inheritTransformRules = modified.inheritTransformRules;
      finalModel.documentStartRules = modified.documentStartRules;
      finalModel.documentEndRules = modified.documentEndRules;
      finalModel.dlfRules = modified.dlfRules;
      finalModel.tables = modified.tables;
      finalModel.version = modified.version;
      finalModel.knownHigherVersion = modified.knownHigherVersion;
      finalModel.created = modified.created;
      finalModel.lastModified = modified.lastModified;
      finalModel.modifiedBy = modified.modifiedBy;
      finalModel.transformationRules = modified.transformationRules;
      finalModel.validators = modified.validators;
      finalModel.comments = modified.comments;
      finalModel.setChanged(modified.changed);
      // finalModel._firstModified = modified._firstModified == undefined ? undefined : { ...modified._firstModified }
    } else {
      finalModel = modified;
    }
    // set in ModelStore
    return ModelStore.set(finalModel, true);
  }

  /**
   * Method determines what AcceleratorDocs (associated with a non-Unknown Model) should be extracted.
   * TODO: fix method name, it describes how this method is used, not what it does
   */
  classifiedeAcceleratorDocsToExtract(
    knownModelKey: ModelStoreKey,
  ): AcceleratorDocStoreKey[] {
    return this.getDocuments(knownModelKey);
  }

  extractModels(
    model: Model,
    docTypeConfig: DocTypeConfig | null,
    priorityDoc: AcceleratorDoc,
  ): [Promise<AcceleratorDoc> | undefined, Promise<AcceleratorDoc>[]] | null {
    if (docTypeConfig == null) {
      //console.warn("DocTypeConfig passed to extractModels was null");
      return null;
    }
    // get all AccDocs with this Model
    let accDocKeys = this.revMap.get(
      this.getReverseMapStoreKey(model.storeId, priorityDoc.storeId),
    );
    if (!accDocKeys) return null;
    // filter out current accDoc
    let priorityDocPromise: Promise<AcceleratorDoc> | undefined;
    accDocKeys = accDocKeys.filter(
      (key: AcceleratorDocStoreKey) =>
        !(
          priorityDoc.storeId.batchInstanceUuid === key.batchInstanceUuid &&
          priorityDoc.storeId.docId === key.docId
        ),
    );
    priorityDocPromise = this.accDocUpdateMethod(
      priorityDoc,
      model,
      docTypeConfig,
      false,
    );

    const promises = accDocKeys
      .map(async (key: AcceleratorDocStoreKey, index) => {
        const acceleratorDoc = AcceleratorDocStore.getWithoutLookup(key);
        console.error(
          `AcceleratorDoc ${stringifyKey(key)} not loaded or hydrated, cannot extract using Model ${stringifyKey(model.storeId)}`,
        );

        if (acceleratorDoc == null) return null;

        return this.accDocUpdateMethod(
          acceleratorDoc,
          model,
          docTypeConfig,
          false,
        );
      })
      .filter((x): x is Promise<AcceleratorDoc> => x !== null);

    return [priorityDocPromise, promises];
  }

  /**
   * Method responsible for correctly updating and extracting the AcceleratorDoc. Document Statuses will be updated
   * after the document has been extracted.
   * @param acceleratorDoc
   * @param model
   * @param docTypeConfig
   */
  private async accDocUpdateMethod(
    acceleratorDoc: AcceleratorDoc,
    model: Model,
    docTypeConfig: DocTypeConfig,
    acceleratorOcrThresholds: boolean,
  ): Promise<AcceleratorDoc> {
    await new AcceleratorDocExtractor(
      acceleratorDoc,
      model,
      docTypeConfig,
      acceleratorOcrThresholds,
    ).extract();

    return acceleratorDoc;
  }

  /**
   * Returns true if the Key was added
   * @param model
   * @param acceleratorDoc
   */
  private addNewDocToReverseMap(
    modelKey: ModelStoreKey,
    acceleratorDocKey: AcceleratorDocStoreKey,
  ): boolean {
    const reverseMapKey = this.getReverseMapStoreKey(
      modelKey,
      acceleratorDocKey,
    );
    if (!this.revMap.has(reverseMapKey)) {
      this.revMap.set(reverseMapKey, []);
    }
    const allAccDocKeys = this.revMap.get(reverseMapKey)!;
    // why isn't this a set ??
    const newKeys = allAccDocKeys.filter(
      (docKey) =>
        docKey.batchInstanceUuid !== acceleratorDocKey.batchInstanceUuid ||
        docKey.docId !== acceleratorDocKey.docId,
    );

    this.revMap.set(reverseMapKey, Array.from([...newKeys, acceleratorDocKey]));
    return newKeys.length === allAccDocKeys.length;
  }

  private removeDocFromReverseMap(
    modelKey: ModelStoreKey,
    acceleratorDocKey: AcceleratorDocStoreKey,
  ) {
    const reverseMapKey = this.getReverseMapStoreKey(
      modelKey,
      acceleratorDocKey,
    );
    if (!this.revMap.has(reverseMapKey)) {
      this.revMap.set(reverseMapKey, []);
    }
    const accDocKeys = this.revMap.get(reverseMapKey)!;
    // filter out accDocKeys
    this.revMap.set(
      reverseMapKey,
      accDocKeys.filter(
        (docKey) =>
          !(
            docKey.batchInstanceUuid === acceleratorDocKey.batchInstanceUuid &&
            docKey.docId === acceleratorDocKey.docId
          ),
      ),
    );
  }

  private getReverseMapStoreKey(
    modelKey: ModelStoreKey,
    acceleratorDocKey: AcceleratorDocStoreKey,
  ): string {
    if (modelKey.name === "") return stringifyKey(acceleratorDocKey);
    return stringifyKey(modelKey);
  }

  //@ts-expect-error
  _vamp(data) {
    // this.map = data.values.map(r => new Model(r));
    // this.revMap = data.revMap;
  }
}
const AcceleratorDocModelStore = new AcceleratorDocModelStoreClass();
export { AcceleratorDocModelStore, AcceleratorDocModelStoreClass };
